<template>
	<n-modal v-model:show="show" preset="card" class="w-[90vw]! max-w-140!" size="large" header-class="py-0! pt-5!">
		<div class="text-fs-xl mb-3 font-medium">{{ $t(`profile.security.totp_modal_title`) }}</div>
		<div class="flex flex-col gap-4">
			<div class="mb-4">
				{{ $t(`profile.security.totp_description`) }}
			</div>

			<Card embedded content-class="flex items-center gap-4.5">
				<span class="bg-primary-200 flex size-9.5 min-w-9.5 items-center justify-center rounded-lg">
					<Icon name="carbon:carbon-for-mobile" :size="16" class="text-primary" />
				</span>
				<span>
					{{ $t(`profile.security.totp_modal_install_app`) }}
				</span>
			</Card>

			<Card embedded content-class="flex items-center gap-4.5">
				<span class="bg-primary-200 flex size-9.5 min-w-9.5 items-center justify-center rounded-lg">
					<Icon name="carbon:qr-code" :size="16" class="text-primary" />
				</span>
				<span>{{ $t(`profile.security.totp_modal_scan_qr`) }}</span>
				<n-spin :show="loading">
					<div class="aspect-square overflow-hidden rounded-lg border bg-white p-2">
						<n-qr-code :value="totpUrl || ''" :padding="0" />
					</div>
				</n-spin>
			</Card>

			<Card embedded content-class="flex flex-col">
				<div class="flex items-center gap-4.5">
					<span class="bg-primary-200 flex size-9.5 min-w-9.5 items-center justify-center rounded-lg">
						<Icon name="carbon:carbon-for-mobile" :size="16" class="text-primary" />
					</span>
					<span>{{ $t(`profile.security.totp_modal_enter_code`) }}</span>
				</div>
				<n-divider />
				<n-form-item :label="$t(`profile.security.temporary_code`)" :show-feedback="false">
					<n-input
						v-model:value="code"
						:disabled="checking"
						:placeholder="$t(`profile.security.temporary_code_placeholder`)"
						clearable
					/>
				</n-form-item>

				<div class="mt-6">
					<JMButton :loading="checking" :disabled="!isValid" class="w-full!" size="large" @click="verify()">
						{{ $t(`profile.security.configure`) }}
					</JMButton>
				</div>
			</Card>
		</div>
	</n-modal>
</template>

<script setup lang="ts">
import type { I18nString } from "@/lang/config"
import { NDivider, NFormItem, NInput, NModal, NQrCode, NSpin, useMessage } from "naive-ui"
import { computed, ref, watch } from "vue"
import { useI18n } from "vue-i18n"
import Api from "@/api"
import Card from "@/components/common/Card.vue"
import Icon from "@/components/common/Icon.vue"
import JMButton from "@/components/common/JMButton.vue"
import { getApiErrorMessage } from "@/utils"

const emit = defineEmits<{
	(e: "success"): void
	(e: "error", error: Error): void
}>()

const show = defineModel<boolean | undefined>("show", { default: false })

const message = useMessage()
const { t } = useI18n()

const loading = ref(false)
const checking = ref(false)
const totpUrl = ref<string | null>(null)
const totpSecret = ref<string | null>(null)
const code = ref<string | null>(null)

const isValid = computed(() => code.value?.trim()?.length === 6)

function getTotpConfig() {
	loading.value = true

	Api.common.auth
		.getTotpConfig()
		.then(res => {
			totpSecret.value = res.data.secret
			totpUrl.value = res.data.url
		})
		.catch(err => {
			show.value = false
			message.error(getApiErrorMessage(err) || t<I18nString>(`common.misc.generic_error`))
			emit("error", err as Error)
		})
		.finally(() => {
			loading.value = false
		})
}

function verify() {
	checking.value = true

	Api.common.auth
		.setTotp({ secret: totpSecret.value || "", otp: code.value || "" })
		.then(() => {
			reset()
			show.value = false
			message.success(t<I18nString>(`profile.security.totp_setup_success`))
			emit("success")
		})
		.catch(err => {
			show.value = false
			message.error(getApiErrorMessage(err) || t<I18nString>(`common.misc.generic_error`))
		})
		.finally(() => {
			checking.value = false
		})
}

function reset() {
	loading.value = false
	checking.value = false
	totpUrl.value = null
	totpSecret.value = null
	code.value = null
}

watch(
	show,
	val => {
		if (val) {
			reset()
			getTotpConfig()
		}
	},
	{ immediate: true }
)
</script>
